import { planAtLeast, WORKFLOW_STEP_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, Skeleton } from "@sagot/ui";
import { useEffect } from "react";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { ConnectorCallStep } from "../types";
import { OperationOutputs } from "./connector-op-outputs";
import { OperationParams } from "./connector-op-params";
import { OperationPicker } from "./connector-op-picker";
import { operationFact, withAllOutputs, withOperation } from "./connector-op-model";
import { OperationTest } from "./connector-op-test";
import { ExitsEditor } from "./exits-editor";
import type { FlowContext } from "./flow-context";
import { VariableList, type FormProps } from "./webhook-form";
import { useOperationSignature } from "./use-connector-ops";

const C = "engage.workflows.connector.op";
const CONNECTOR_PLAN = WORKFLOW_STEP_MIN_PLAN.http_request ?? "pro";
const FACING_WAIT_SECONDS = 5;

/** Where the call runs: behind the scenes, or while the visitor waits (one attempt, a few seconds at most). */
function RunsNote({ facing }: { facing: boolean }) {
  const { t } = useTranslation();
  return (
    <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="op-runs-note">
      <Icon name="info" className="mt-0.5 size-3.5 shrink-0" />
      {t(facing ? `${C}.runsFacing` : `${C}.runsBehind`, { seconds: FACING_WAIT_SECONDS })}
    </p>
  );
}

function FactsNote({ step }: { step: ConnectorCallStep }) {
  const { t } = useTranslation();
  return (
    <p className="text-xs text-fg-subtle" data-testid="op-facts">
      {t(`${C}.facts`)}{" "}
      {(["ok", "status", "error"] as const).map((fact, index) => (
        <span key={fact}>
          {index > 0 ? ", " : ""}
          <code className="font-mono">{operationFact(step, fact)}</code>
        </span>
      ))}
    </p>
  );
}

/**
 * "Call connector operation" (SD-SC-12): the operation of a connector of the Connectors platform, its typed parameters
 * filled from the run, its declared outputs copied into run variables, a test through the Connectors test console and the
 * five exits (worked, not found, refused, unavailable, anything else).
 */
export function OperationForm({ step, disabled, onChange, paths, canAddPath, facing = false }: FormProps<ConnectorCallStep> & Pick<FlowContext, "paths" | "canAddPath" | "facing">) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const locked = plan !== undefined && !planAtLeast(plan, CONNECTOR_PLAN);
  const planName = t(`console.plans.${CONNECTOR_PLAN}`);
  const signature = useOperationSignature(step.connector_id, step.operation_key);
  const chosen = step.connector_id !== "" && step.operation_key !== "";
  const declared = signature.data?.outputs;
  // The outputs an operation declares are written into the step once it is open for editing, so conditions can name the variables.
  useEffect(() => {
    if (declared === undefined || disabled) return;
    const adopted = withAllOutputs(step, declared);
    if (adopted !== step) onChange(adopted);
  }, [declared, disabled, step, onChange]);
  return (
    <div className="space-y-5" data-testid="op-form">
      {locked ? (
        <p className="flex items-center gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note" data-testid="op-plan-lock">
          <Icon name="lock" className="size-4 shrink-0" />
          {t(`${C}.plan`, { plan: planName })}
        </p>
      ) : null}
      <RunsNote facing={facing} />
      <OperationPicker step={step} disabled={disabled} signature={signature.data} onPick={(connectorId, key) => { onChange(withOperation(step, connectorId, key)); }} />
      {chosen && signature.isPending ? (
        <div role="status" aria-label={t(`${C}.loadingSignature`)} className="space-y-2" data-testid="op-signature-loading">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}
      {chosen && signature.isError ? <p role="alert" className="text-xs font-semibold text-danger" data-testid="op-signature-error">{t(`${C}.signatureFailed`)}</p> : null}
      {chosen && signature.data ? (
        <>
          <OperationParams step={step} specs={signature.data.params} disabled={disabled} onChange={onChange} />
          <div>
            <VariableList />
          </div>
          <OperationOutputs step={step} declared={signature.data.outputs} disabled={disabled} onChange={onChange} />
          <FactsNote step={step} />
        </>
      ) : null}
      <OperationTest step={step} locked={locked} planName={planName} disabled={disabled} />
      <ExitsEditor step={step} disabled={disabled} onChange={onChange} paths={paths} canAddPath={canAddPath} />
    </div>
  );
}
